// src/rms/pages/SettingsHours.jsx
// ============================================================================
// Settings → Hours
// ============================================================================
// The ordinary week, and the dates that break it.
//
// Two things this screen deliberately does NOT do.
//
// It does not make you fill in seven days before anything works. A business
// with no rows here is open, always -- see the header of 20260821100000 -- so
// the screen opens usable and an operator who only ever ticks "closed on
// Sunday" has configured something correct and complete.
//
// It does not clear the times when a day is marked closed. Untick "closed" next
// spring and last year's hours are still there, which is what somebody who
// shuts for the winter actually wants. The CHECK constraint permits it: closed
// days may keep their times.
// ============================================================================

import React, { useCallback, useEffect, useState } from "react";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { WEEKDAYS, dateKey } from "../pricing/hours";
import { Field, TextInput, EmptyState, formatDate } from "../components/ui";

/** A row per weekday, whether or not the database has one yet. */
function weekFrom(rows) {
  return WEEKDAYS.map((d) => {
    const found = rows.find((r) => Number(r.weekday) === d.value);
    return {
      ...d,
      id: found?.id || null,
      is_closed: found ? !!found.is_closed : false,
      opens_at: (found?.opens_at || "09:00").slice(0, 5),
      closes_at: (found?.closes_at || "17:00").slice(0, 5),
      configured: !!found,
    };
  });
}

export default function SettingsHours({ onNotice }) {
  const { operator, t } = useRms();

  const [week, setWeek] = useState(() => weekFrom([]));
  const [exceptions, setExceptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    const [hours, exc] = await Promise.all([
      db.list("operatingHours", { operator_id: operator.id }),
      db.list("hoursExceptions", { operator_id: operator.id }),
    ]);
    setWeek(weekFrom(hours.data || []));
    setExceptions(
      [...(exc.data || [])].sort((a, b) => String(a.on_date).localeCompare(String(b.on_date)))
    );
  }, [operator.id]);

  useEffect(() => {
    load();
  }, [load]);

  const setDay = (weekday, patch) =>
    setWeek((w) => w.map((d) => (d.value === weekday ? { ...d, ...patch } : d)));

  const saveWeek = async () => {
    setBusy(true);
    for (const day of week) {
      const row = {
        operator_id: operator.id,
        weekday: day.value,
        is_closed: day.is_closed,
        opens_at: day.opens_at,
        closes_at: day.closes_at,
      };
      // Upsert by hand: the adapters expose create/update, not an upsert, and
      // the UNIQUE (operator_id, weekday) is what makes this safe to repeat.
      if (day.id) await db.update("operatingHours", day.id, row);
      else await db.create("operatingHours", row);
    }
    await load();
    setBusy(false);
    onNotice?.(t("hours.saved", "Opening hours saved."));
  };

  const saveException = async () => {
    if (!draft.on_date) return;
    const row = {
      operator_id: operator.id,
      on_date: draft.on_date,
      label: draft.label || null,
      is_closed: draft.is_closed,
      opens_at: draft.is_closed ? null : draft.opens_at,
      closes_at: draft.is_closed ? null : draft.closes_at,
    };
    const result = draft.id
      ? await db.update("hoursExceptions", draft.id, row)
      : await db.create("hoursExceptions", row);

    if (!result.success) {
      onNotice?.({ tone: "danger", text: result.error });
      return;
    }
    setDraft(null);
    await load();
    onNotice?.(t("hours.dateSaved", "Date saved."));
  };

  const removeException = async (id) => {
    await db.remove("hoursExceptions", id);
    await load();
  };

  return (
    <>
      <div className="rms-card rms-card-pad" style={{ marginBottom: 18 }}>
        <h3 className="rms-section-title">{t("hours.week", "The ordinary week")}</h3>
        <p className="rms-help" style={{ marginTop: -6, marginBottom: 14 }}>
          {t("hours.weekHelp", "Times are local to your business. A day you never touch stays open all day.")}
        </p>

        <table className="rms-table">
          <tbody>
            {week.map((day) => (
              <tr key={day.value}>
                <td style={{ width: 120 }}>
                  <strong>{t(day.key, day.label)}</strong>
                </td>
                <td style={{ width: 130 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={day.is_closed}
                      onChange={(e) => setDay(day.value, { is_closed: e.target.checked })}
                    />
                    {t("hours.closed", "Closed")}
                  </label>
                </td>
                <td>
                  {/* Left in place, and left editable, on a closed day. Somebody
                      who shuts for the season should find their hours waiting
                      when they open again. */}
                  <div style={{ display: "flex", gap: 6, alignItems: "center", opacity: day.is_closed ? 0.45 : 1 }}>
                    <input
                      type="time"
                      className="rms-input"
                      style={{ maxWidth: 130 }}
                      value={day.opens_at}
                      onChange={(e) => setDay(day.value, { opens_at: e.target.value })}
                      aria-label={t("hours.opens", "Opens")}
                    />
                    <span style={{ color: "var(--rms-muted)", fontSize: 13 }}>{t("hours.to", "to")}</span>
                    <input
                      type="time"
                      className="rms-input"
                      style={{ maxWidth: 130 }}
                      value={day.closes_at}
                      onChange={(e) => setDay(day.value, { closes_at: e.target.value })}
                      aria-label={t("hours.closes", "Closes")}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          type="button"
          className="rms-btn rms-btn--primary"
          style={{ marginTop: 14 }}
          onClick={saveWeek}
          disabled={busy}
        >
          {busy ? t("common.working", "Working…") : t("hours.saveWeek", "Save the week")}
        </button>
      </div>

      <div className="rms-card rms-card-pad">
        <h3 className="rms-section-title">{t("hours.exceptions", "Closures and short days")}</h3>
        <p className="rms-help" style={{ marginTop: -6, marginBottom: 14 }}>
          {t("hours.exceptionsHelp", "Public holidays, a seasonal shutdown, a half day. These beat the weekly pattern.")}
        </p>

        {exceptions.length === 0 ? (
          <EmptyState
            title={t("hours.noExceptions", "No dates yet")}
            message={t("hours.noExceptionsBody", "Add the days your week does not describe.")}
          />
        ) : (
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("hours.date", "Date")}</th>
                <th>{t("hours.label", "What is it")}</th>
                <th>{t("hours.opening", "Opening")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {exceptions.map((e) => (
                <tr key={e.id}>
                  <td><strong>{formatDate(`${e.on_date}T12:00:00`)}</strong></td>
                  <td style={{ fontSize: 12.5 }}>{e.label || "—"}</td>
                  <td style={{ fontSize: 12.5 }}>
                    {e.is_closed
                      ? t("hours.closedAllDay", "Closed all day")
                      : `${(e.opens_at || "").slice(0, 5)} – ${(e.closes_at || "").slice(0, 5)}`}
                  </td>
                  <td className="rms-num" style={{ whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="rms-btn rms-btn--ghost rms-btn--sm"
                      onClick={() => setDraft({ ...e, opens_at: (e.opens_at || "09:00").slice(0, 5), closes_at: (e.closes_at || "17:00").slice(0, 5) })}
                    >
                      {t("action.edit", "Edit")}
                    </button>{" "}
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => removeException(e.id)}>
                      {t("campaignForm.remove", "Remove")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {draft ? (
          <div style={{ borderTop: "1px solid var(--rms-border)", marginTop: 16, paddingTop: 16 }}>
            <div className="rms-field-grid">
              <Field label={t("hours.date", "Date")} required>
                <input
                  type="date"
                  className="rms-input"
                  value={draft.on_date || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, on_date: e.target.value }))}
                />
              </Field>
              <Field label={t("hours.label", "What is it")}>
                <TextInput
                  value={draft.label || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, label: v }))}
                  placeholder={t("hours.labelPlaceholder", "Christmas Day")}
                />
              </Field>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={draft.is_closed}
                onChange={(e) => setDraft((d) => ({ ...d, is_closed: e.target.checked }))}
              />
              {t("hours.closedAllDay", "Closed all day")}
            </label>

            {!draft.is_closed ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12 }}>
                <input
                  type="time"
                  className="rms-input"
                  style={{ maxWidth: 130 }}
                  value={draft.opens_at}
                  onChange={(e) => setDraft((d) => ({ ...d, opens_at: e.target.value }))}
                  aria-label={t("hours.opens", "Opens")}
                />
                <span style={{ color: "var(--rms-muted)", fontSize: 13 }}>{t("hours.to", "to")}</span>
                <input
                  type="time"
                  className="rms-input"
                  style={{ maxWidth: 130 }}
                  value={draft.closes_at}
                  onChange={(e) => setDraft((d) => ({ ...d, closes_at: e.target.value }))}
                  aria-label={t("hours.closes", "Closes")}
                />
              </div>
            ) : null}

            <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={saveException}>
              {t("action.save", "Save")}
            </button>{" "}
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setDraft(null)}>
              {t("action.cancel", "Cancel")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="rms-btn rms-btn--ghost rms-btn--sm"
            style={{ marginTop: 14 }}
            onClick={() =>
              setDraft({
                on_date: dateKey(new Date()),
                label: "",
                is_closed: true,
                opens_at: "09:00",
                closes_at: "17:00",
              })
            }
          >
            {t("hours.addDate", "Add a date")}
          </button>
        )}
      </div>
    </>
  );
}
